import {
  Dispatch,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from "react";

import { useGetAuthUser } from "@/api/get-user";
import { useIsSuperAdmin } from "@/api/is-superadmin";
import { LoadingScreen } from "@/components/loading-screen";
import { useSubmitUserVisit } from "@/features/reporting/api/submit-user-visit";
import { RoleEnum } from "@classview/shared-types";
import * as Sentry from "@sentry/react";

import { useMe } from "../api/get-my-organisations";
import { UserOrganisation } from "../types/user-organisation";

type OrganisationContextState = {
  organisation: UserOrganisation;
  setOrganisation: Dispatch<SetStateAction<UserOrganisation>>;
  hasPermission: (permission: RoleEnum | RoleEnum[]) => boolean;
};

const defaultState: OrganisationContextState = {
  // @ts-expect-error initial context state
  organisation: null,
  setOrganisation: () => {},
  hasPermission: () => false,
};

const OrganisationContext =
  createContext<OrganisationContextState>(defaultState);

const STORAGE_KEY = "classview-organisation";

function OrganisationProvider({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading, isSuccess } = useMe();
  const { isSuperAdmin, isLoading: isSuperAdminLoading } = useIsSuperAdmin();
  const [organisation, setOrganisation] = useState<UserOrganisation>(
    defaultState.organisation,
  );
  const { data: user } = useGetAuthUser();
  const { mutate: submitUserVisit } = useSubmitUserVisit();

  useEffect(() => {
    if (!isSuccess || isSuperAdminLoading) return;

    const organisations = me.organisations;

    try {
      const savedOrganisationString = localStorage.getItem(STORAGE_KEY);

      const savedOrganisation = JSON.parse(
        savedOrganisationString ?? "null",
      ) as unknown as UserOrganisation | null;
      /**
       * If there is no saved organisation, set the first organisation in the list
       */
      if (!savedOrganisation) {
        setOrganisation(organisations[0]);

        return;
      }

      /**
       * Check if the saved organisation is still in the list of the user
       */
      const updatedOrganisation = organisations.find(
        (o) => o.id === savedOrganisation.id,
      );

      /**
       * If the saved organisation is not in the list of the user organisations, remove it
       */
      if (!updatedOrganisation) {
        localStorage.removeItem(STORAGE_KEY);

        if (organisations.length > 0) {
          setOrganisation(organisations[0]);
        }
      } else {
        setOrganisation(updatedOrganisation);
      }
    } catch {
      // whatever caused the error, remove the saved organisation
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [isSuccess, me, setOrganisation, isSuperAdminLoading]);

  useEffect(() => {
    if (organisation) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(organisation));
      Sentry.setTag("organisation", organisation.id);
    } else {
      Sentry.setTag("organisation", null);
    }
  }, [organisation]);

  useEffect(() => {
    if (organisation && user && user.sub) {
      submitUserVisit({
        organisationId: organisation.id,
        userId: user.sub,
      });
    }
  }, [organisation, submitUserVisit, user]);

  function hasPermission(permission: RoleEnum | RoleEnum[]) {
    if (isSuperAdmin) return true;

    if (Array.isArray(permission)) {
      return permission.some((p) => organisation.roles.includes(p));
    }

    return organisation.roles.includes(permission);
  }

  return (
    <OrganisationContext.Provider
      value={{ organisation, setOrganisation, hasPermission }}
    >
      {/*
       * If there is no organisation, show the loading screen
       * But also show it if the user is still loading,
       * because we need to wait for the user to load before we can check
       * if the stored organisation is still in the list of the user organisations
       */}
      {!organisation || isLoading || isSuperAdminLoading ? (
        <LoadingScreen />
      ) : (
        children
      )}
    </OrganisationContext.Provider>
  );
}

export { OrganisationContext, OrganisationProvider };
